import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, lastValueFrom, Observable, take } from 'rxjs';
import { AccountsModel } from 'sharedFeatures/sharedModels/xero/accountsModel';
import { AccountsGroup } from 'sharedFeatures/sharedModels/xero/accountsGroupModel';
import { AssociationsGroup } from 'sharedFeatures/sharedModels/xero/associationsGroupModel';
import { AssociationsModel } from "sharedFeatures/sharedModels/xero/associationsModel";
import { AssociationsSubGroup } from 'sharedFeatures/sharedModels/xero/associationsSubGroupModel';

import { XeroImport } from "sharedFeatures/sharedModels/XeroImport";
import { Utils } from "sharedGuiSmall/utils/utils";
import { select } from '@angular-redux/store';
import { ErrorInterceptor } from 'sharedFeatures/http';

declare let handleAngularHttpError: any;

@Injectable()
export class XeroService {
    private appComponent: any = null;

    @select(['appData', 'appComponent'])
    readonly appComponent$!: Observable<any>;

    constructor(private http: HttpClient) {
        this.appComponent$.pipe(
            filter(x => !!x),
            take(1),)
            .subscribe((appComponent: any) => {
                this.appComponent = appComponent;
            });
    }

    public ShowXeroError() {
        this.appComponent.showErrorDialog(ErrorInterceptor.xeroErrorMessageText, () => {
            document.location.reload();
        }, { keepParent: true, isModal: true }, true);
    }

    CheckData(res: string, allowEmptyResult: boolean = false) { 
        let data = { success: false };

        if ((res && res.length)) {
            if (res.indexOf("error=sessiontimeout") === -1)
                data = JSON.parse(res);
        }
        else {
            if (!allowEmptyResult)
                this.ShowXeroError();
        }

        return data;
    }

    public SetAccountsMatch(data: AccountsModel, matchType: number): Promise<any> {

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json; charset=utf-8');

        const params = {
            AccountsList: JSON.stringify(data),
            matchType
        };

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/SetAccountsMatch', params, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);
                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('SetAccountsMatch error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });

    }

    // Get all Profit & Loss based Xero accounts from the Xero company
    public GetAccountsMatch(matchType: number): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("matchType", matchType.toString());

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/GetAccountsMatch', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('GetAccountsMatch error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    // Get imported Profit & Loss based Xero accounts from the plan Options
    public GetImportedAccountsMatch(planGUID: string, matchType: number): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("matchType", matchType.toString())
            .set("planGUID", planGUID);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/GetImportedAccountsMatch', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('GetImportedAccountsMatch error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    // refresh plan objects with current Xero objects list
    public XeroRefreshPlan(planGUID: string, isCurrentMonthActuals: boolean, connectToXero: boolean = false): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("planGUID", planGUID)
            .set("isCurrentMonthActuals", isCurrentMonthActuals + '')
            .set("connectToXero", connectToXero + '');

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/RefreshPlan', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero RefreshPlan error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    // refresh plan with current Xero COA
    public XeroRefreshCOA(planGUID: string, plAccounts?: string, bsAccounts?: string): Promise<any> {

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json; charset=utf-8');

        const params = {
            planGUID,
            plAccounts: plAccounts,
            bsAccounts: bsAccounts
        };

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/RefreshCOA', params, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero RefreshCOA error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    // refresh OB
    public XeroRefreshOB(planGUID: string): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("planGUID", planGUID);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/RefreshOB', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero RefreshOB error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    // get component associations for newly imported plan
    public XeroGetImportedComponentAssociations(isConnectedToXero: boolean, planGUID?: string, plAccounts?: AccountsModel, bsAccounts?: AccountsModel): Promise<any> {

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json; charset=utf-8');

        let plAccountsGroups: AccountsGroup[] | undefined;
        let bsAccountsGroups: AccountsGroup[] | undefined;
        if (plAccounts && bsAccounts) {
            plAccountsGroups = Object.entries(plAccounts).map<AccountsGroup>(([groupName, accounts]) => ({ GroupName: groupName, Accounts: accounts }));
            bsAccountsGroups = Object.entries(bsAccounts).map<AccountsGroup>(([groupName, accounts]) => ({ GroupName: groupName, Accounts: accounts }));
        }

        const params = {
            planGuid: planGUID,
            isConnectedToXero,
            plAccountsGroups,
            bsAccountsGroups
        }

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/GetImportedComponentAssociations', params, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero GetImportedComponentAssociations' + ' error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    public XeroGetComponentAssociations(plAccounts?: AccountsModel, bsAccounts?: AccountsModel): Promise<any> {
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json; charset=utf-8');

        let plAccountsGroups: AccountsGroup[] | undefined;
        let bsAccountsGroups: AccountsGroup[] | undefined;
        if (plAccounts && bsAccounts) {
            plAccountsGroups = Object.entries(plAccounts).map<AccountsGroup>(([groupName, accounts]) => ({ GroupName: groupName, Accounts: accounts }));
            bsAccountsGroups = Object.entries(bsAccounts).map<AccountsGroup>(([groupName, accounts]) => ({ GroupName: groupName, Accounts: accounts }));
        }

        const params = {
            plAccounts: plAccountsGroups,
            bsAccounts: bsAccountsGroups
        }

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/GetComponentAssociations', params, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero GetComponentAssociations' + ' error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    public SetImportedComponentAssociations(data: AssociationsModel, planGUID?: string): Promise<any> {

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json; charset=utf-8');

        let associations: AssociationsGroup[] | undefined;

        associations = Object.entries(data).map<AssociationsGroup>(([groupName, subgroup]) => ({
            GroupName: groupName, SubGroups: Object.entries(subgroup).map<AssociationsSubGroup>(([subgroupName, associations]) => ({ SubGroupName: subgroupName, Associations: associations }))
        }));

        const params = {
            componentAssociations: associations,
            planGuid: planGUID
        };

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/SetImportedComponentAssociations', params, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('SetImportedComponentAssociations' + ' error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    public SetComponentAssociations(data: AssociationsModel, plAccounts?: AccountsModel, bsAccounts?: AccountsModel): Promise<any> {

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json; charset=utf-8');

        let associations: AssociationsGroup[] | undefined;
        let plAccountsGroups: AccountsGroup[] | undefined;
        let bsAccountsGroups: AccountsGroup[] | undefined;

        associations = Object.entries(data).map<AssociationsGroup>(([groupName, subgroup]) => ({
            GroupName: groupName, SubGroups: Object.entries(subgroup).map<AssociationsSubGroup>(([subgroupName, associations]) => ({ SubGroupName: subgroupName, Associations: associations }))
        }));

        if (plAccounts && bsAccounts) {
            plAccountsGroups = Object.entries(plAccounts).map<AccountsGroup>(([groupName, accounts]) => ({ GroupName: groupName, Accounts: accounts }));
            bsAccountsGroups = Object.entries(bsAccounts).map<AccountsGroup>(([groupName, accounts]) => ({ GroupName: groupName, Accounts: accounts }));
        }

        const params = {
            componentAssociations: associations,
            plAccounts: plAccountsGroups,
            bsAccounts: bsAccountsGroups
        };

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/SetComponentAssociations', params, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('SetComponentAssociations error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });

    }

    public DisconnectXeroPlan(planGUID: string, callback?: Function): Promise<any> {

        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("planGuid", planGUID);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/DisconnectXeroPlan', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {

                    let data = this.CheckData(res);

                    if (callback) callback();

                    resolve(data);

                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero/DisconnectXeroPlan error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });

    }

    public ConnectXeroPlan(planGUID: string, callback?: Function): Promise<any> {

        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("planGuid", planGUID);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/ConnectXeroPlan', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {

                    let data = this.CheckData(res);

                    if (callback) callback();

                    resolve(data);

                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero/ConnectXeroPlan error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });

    }

    // Get connected xero companies list & current company id
    public GetCompaniesList(): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams();
        //.set("matchType", matchType.toString());

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/GetConnectedCompaniesList', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('GetCompaniesList error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    // change current company
    public ChangeCurrentCompany(companyId: string): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams();

        params = new HttpParams().set("companyId", companyId);
        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/ChangeCurrentCompany', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero ChangeCurrentCompany error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    // Check plan sync status
    public XeroCheckSyncStatus(planGUID: string): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("planGUID", planGUID);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/CheckSyncStatus', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero CheckSyncStatus error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    //Get SameXeroCompanyPlansCount
    public GetSameXeroCompanyPlansCount(planGUID: string, userID: string): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("planGUID", planGUID)
            .set("userID", userID);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/SameXeroCompanyPlansCount', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero SameXeroOrganizationPlansCount error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    //Get XeroLimits
    public GetXeroLimits(customerId: string, planGuid: string, checkXeroToken: boolean = false): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("customerId", customerId)
            .set("planGuid", planGuid);

        if (checkXeroToken)
            params = new HttpParams()
                .set("customerId", customerId)
                .set("planGuid", planGuid)
                .set("checkXeroToken", "true");

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/GetXeroLimits', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res, true);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero GetXeroLimits error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    //Get GetXeroLimitsByCompanyId
    public GetXeroLimitsByCompanyId(xeroCompanyId: string): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("xeroCompanyId", xeroCompanyId);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/GetXeroLimitsByCompanyId', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res, true);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero GetXeroLimitsByCompanyId error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    //Get XeroImports
    public GetXeroImports(customerId: string, planGuid: string): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("customerId", customerId)
            .set("planGuid", planGuid);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/GetXeroImports', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero GetXeroImports error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    //DisconnectXeroCompany
    public DisconnectXeroCompany(customerId: string): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("customerId", customerId);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/DisconnectXeroCompany', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero GetXeroImports error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    public SaveImport(data: XeroImport): Promise<any> {

        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("import", JSON.stringify(data));

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/SaveImport', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('SaveImport error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });

    }

    public SwitchUpdate(planGuid: string, type: string, value: string): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("planGuid", planGuid)
            .set("type", type)
            .set("value", value);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/SwitchUpdate', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('SwitchMidnightUpdate error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });

    }

    // Refresh plan with data passed from Azure import funtion
    public RefreshPlanUsingSyncData(planGUID: string, isCashflowSyncActive: boolean, isPLSyncActive: boolean, importType: string = ''): Promise<any> {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
        let params = new HttpParams()
            .set("PlanGUID", planGUID)
            .set("IsCashflowSyncActive", isCashflowSyncActive.toString())
            .set("isPLSyncActive", isPLSyncActive.toString());

        if (!Utils.isNullOrEmpty(importType))
            params = new HttpParams()
                .set("PlanGUID", planGUID)
                .set("IsCashflowSyncActive", isCashflowSyncActive.toString())
                .set("isPLSyncActive", isPLSyncActive.toString())
                .set("importType", importType);

        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Xero/RefreshPlanUsingSyncData', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = this.CheckData(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('Xero RefreshPlanUsingSyncData error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

}
